/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getBraindateConversation,
  getBraindateCapacity,
  getBraindateStartTime,
} from '@e180/domain/lib/braindate/util';

import styles from './BraindateCardCheckInStatus.style';

import Time from 'src/shared/app/base/date/component/Time';
import messages from 'src/shared/app/braindate/l10n/braindateL10n';
import {
  hasUserCheckedIn,
  getCheckedInOtherParticipants,
  getMissingOtherParticipants,
} from 'src/shared/app/check-in/util/checkInUtil';
import { groupBraindateMaxParticipantCount } from 'src/shared/domain/braindate/settings/braindateSettings';
import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { User } from '@e180/domain/lib/user/type';

type Props = {
  braindate: Braindate,
  user: User,
  showOwnCheckInStatus?: boolean,
};

const useStyles = createUseStyles(styles);

const BraindateCardCheckInStatus = ({
  braindate,
  user,
  showOwnCheckInStatus,
}: Props): React.Node => {
  const isGroupBraindate = isConversationAboutGroupBraindate(
    getBraindateConversation(braindate),
  );

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  let statusTxt;

  if (showOwnCheckInStatus) {
    statusTxt = intl.formatMessage(
      hasUserCheckedIn(braindate, user)
        ? messages.selfCheckedIn
        : messages.selfNotCheckedIn,
    );
  } else {
    const { length: checkedInCount } = getCheckedInOtherParticipants(
      braindate,
      user,
    );
    const { length: missingCount } = getMissingOtherParticipants(
      braindate,
      user,
    );

    if (isGroupBraindate) {
      const groupCapacity = getBraindateCapacity(braindate);

      if (
        (groupCapacity && checkedInCount === groupCapacity - 1) ||
        checkedInCount === groupBraindateMaxParticipantCount - 1
      ) {
        statusTxt = intl.formatMessage(messages.everybody);
      } else if (checkedInCount === 0) {
        statusTxt = intl.formatMessage(messages.nobody);
      } else {
        statusTxt = intl.formatMessage(messages.some, {
          count: checkedInCount,
          total: groupBraindateMaxParticipantCount - 1,
        });
      }
    } else if (checkedInCount) {
      statusTxt = intl.formatMessage(messages.checkedIn);
    } else if (missingCount) {
      statusTxt = intl.formatMessage(messages.notCheckedIn);
    }
  }

  return (
    <span className={classes.root}>
      <Time date={getBraindateStartTime(braindate)} />
      <span>{statusTxt}</span>
    </span>
  );
};

export default compose(React.memo)(BraindateCardCheckInStatus);
